import { DI } from '@microsoft/fast-foundation';
import { Connect, logger } from '@genesislcap/foundation-comms';
import { AnalyticsPreferences, VppaRankings } from '../types';
import { IVppaService } from './vppa.service';
import { observable, Observable } from '@microsoft/fast-element';
import { PRODUCT_TYPE } from '../utils';

export type Step =  1 | 2 | 3;

export interface IBuyerAnalyticsService {
  currentStep: 1 | 2 | 3;
  selectedVppas: VppaRankings[];
  stepTwoSelectedVppas: VppaRankings[];
  currentPreferences: AnalyticsPreferences;
  productType: PRODUCT_TYPE;
  vppaRankings: VppaRankings[];
  setVppaRankings(preferences: AnalyticsPreferences);
  assignVppaRankings(vppaRankings: VppaRankings[]): void;
  resetSelectedOptions(): void;
  getSelectedVppaRankings(): VppaRankings[];
  filterVppaRankings(vppaIds: Array<any>): VppaRankings[];
}

class BuyerAnalyticsServiceImpl implements IBuyerAnalyticsService {
  @IVppaService vppaService: IVppaService;

  @observable productType: PRODUCT_TYPE;

  private _currentStep: Step = 1;
  private _currentPreferences: AnalyticsPreferences;
  private _selectedVppas: VppaRankings[] = [] as VppaRankings[];
  private _stepTwoSelectedVppas: VppaRankings[] = [] as VppaRankings[];
  private _vppaRankings: VppaRankings[] = [] as VppaRankings[];

  constructor(@Connect private connect: Connect) {}

  get currentStep() {
    Observable.track(this, 'currentStep');
    if (sessionStorage.getItem('buyerCurrentStep')) {
        return parseInt(sessionStorage.getItem('buyerCurrentStep')) as Step;
    } else {
        return this._currentStep;
    }
  }

  set currentStep(step: Step) {
    sessionStorage.setItem('buyerCurrentStep', step.toString())
    this._currentStep = step;
    Observable.notify(this, 'currentStep');
  }

  get currentPreferences() {
    Observable.track(this, 'analyticsPreferences');
    if (sessionStorage.getItem('analyticsPreferences')) {
        return JSON.parse(sessionStorage.getItem('analyticsPreferences'));
    } else {
        return this._currentPreferences;
    }
  }

  set currentPreferences(preference: AnalyticsPreferences) {
    if (preference) {
        sessionStorage.setItem('analyticsPreferences', JSON.stringify(preference))
    } else {
        sessionStorage.removeItem('analyticsPreferences')
    }
    this._currentPreferences = preference;
    Observable.notify(this, 'currentPreferences');
  }

  get selectedVppas() {
    Observable.track(this, 'selectedVppas');
    if (sessionStorage.getItem('buyerSelectedVppas')) {
        return JSON.parse(sessionStorage.getItem('buyerSelectedVppas'));
    } else {
        return this._selectedVppas;
    }
  }

  set selectedVppas(vppas: VppaRankings[]) {
    sessionStorage.setItem('buyerSelectedVppas', JSON.stringify(vppas))
    this._selectedVppas = vppas;
    Observable.notify(this, 'selectedVppas');
  }

  get stepTwoSelectedVppas() {
    Observable.track(this, 'stepTwoSelectedVppas');
    if (sessionStorage.getItem('buyerSelectedVppasStepTwo')) {
        return JSON.parse(sessionStorage.getItem('buyerSelectedVppasStepTwo'));
    } else {
        return this._stepTwoSelectedVppas;
    }
  }

  set stepTwoSelectedVppas(vppas: VppaRankings[]) {
    sessionStorage.setItem('buyerSelectedVppasStepTwo', JSON.stringify(vppas))
    this._stepTwoSelectedVppas = vppas;
    Observable.notify(this, 'stepTwoSelectedVppas');
  }

 get vppaRankings() {
    Observable.track(this, 'vppaRankings');
    if (sessionStorage.getItem('buyerVppaRankings')) {
        return JSON.parse(sessionStorage.getItem('buyerVppaRankings'));
    } else {
        return this._stepTwoSelectedVppas;
    }
  }

  set vppaRankings(vppas: VppaRankings[]) {
    sessionStorage.setItem('buyerVppaRankings', JSON.stringify(vppas))
    this._vppaRankings = vppas;
    Observable.notify(this, 'vppaRankings');
  }

  public resetSelectedOptions() {
    this.selectedVppas = []
    this.vppaRankings = []
    this.stepTwoSelectedVppas = []
    this._currentPreferences = undefined
    // removing from storage to reset preferences value
    sessionStorage.removeItem('analyticsPreferences')
    Observable.notify(this, 'currentPreferences')
    this.currentStep = 1
  }

  public assignVppaRankings(vppaRankings?: VppaRankings[]) {
    this.vppaRankings = vppaRankings
      ? vppaRankings.length > 20
        ? vppaRankings.slice(0, 20)
        : vppaRankings
      : [];
  }

  public setVppaRankings(preference: AnalyticsPreferences) {
    this.currentPreferences = preference;
    this.vppaService
      .getVppaRanking(preference)
      .then((result) => {
        if (result.REPLY && result.REPLY.length > 0) {
          this.assignVppaRankings(result.REPLY);
        } else {
          this.assignVppaRankings();
        }
      })
      .catch((error) => {
        this.assignVppaRankings();
        logger.error(error);
      });
  }

  public getSelectedVppaRankings() {
    return this.selectedVppas;
  }

  public filterVppaRankings(vppaHeaders: Array<any>): VppaRankings[] {
    const vppaUniqueIds = vppaHeaders.map((header) => header.substring(0, header.indexOf('-')));
    this.selectedVppas = this.vppaRankings.filter(
      (vppaRanking) => vppaUniqueIds.indexOf(vppaRanking.UNIQUE_ID) >= 0
    );
    return this.selectedVppas;
  }
}

export const IBuyerAnalyticsService = DI.createInterface<IBuyerAnalyticsService>((x) =>
  x.singleton(BuyerAnalyticsServiceImpl)
);
